"use client";

import React, { useMemo } from "react";

import { ROLES_CAN_SEE_FILES } from "@/providers/access/access.constants";
import RoleBasedRender from "@/providers/access/role-based-render";
import { useGetDistrictLevelsQuery } from "@/providers/store/api/districts/districts";
import { useAppSelector } from "@/providers/store/hooks";
import { schoolTypeLabelsFromLevelIds } from "@/shared/utils/school-level-display.utils";

import { type ISchoolPayload } from "../../map-panel-context";

import { BasicInformation } from "./basic-information";
import { buildSchoolDriveHref } from "./build-school-drive-href";
import { Documents } from "./documents";
import { SectionAttendanceZoneBoundary } from "./section-attendance-zone-boundary";

function schoolLevelIdsFromPayload(payload: ISchoolPayload | null): string[] {
  return payload?.schoolLevelIds ?? [];
}

interface SchoolPanelContentProps {
  payload: ISchoolPayload | null;
}

const SchoolPanelContent: React.FC<SchoolPanelContentProps> = React.memo(
  ({ payload }) => {
    const districtId = useAppSelector((state) => state.district.selectedDistrict?.id ?? null);
    const levelIdsForQuery = schoolLevelIdsFromPayload(payload);
    const { data: districtLevels } = useGetDistrictLevelsQuery(
      { districtId: districtId ?? "" },
      { skip: !districtId || levelIdsForQuery.length === 0 },
    );

    const schoolTypeLabels = useMemo(() => {
      if (payload?.schoolTypeLabels?.length) {
        return payload.schoolTypeLabels;
      }
      return schoolTypeLabelsFromLevelIds(levelIdsForQuery, districtLevels);
    }, [payload?.schoolTypeLabels, levelIdsForQuery, districtLevels]);

    return (
      <div className="space-y-6 px-3 py-4">
        <BasicInformation
          address={payload?.address ?? "-"}
          capacity={payload?.capacity ?? "-"}
          closedAt={payload?.closedAt ?? undefined}
          educationalProgramType={payload?.educationalProgramType}
          featureAction={payload?.futureAction ?? null}
          gradeServed={payload?.gradeServed ?? "-"}
          name={payload?.name ?? "-"}
          phoneNumber={payload?.phoneNumber ?? "-"}
          principalName={payload?.principalName ?? "-"}
          schoolDriveId={payload?.schoolDriveId}
          schoolId={payload?.schoolId ?? "-"}
          schoolTypeLabels={schoolTypeLabels.length > 0 ? schoolTypeLabels : undefined}
          schoolURL={payload?.schoolURL ?? ""}
          schoolURLHref={payload?.schoolURLHref ?? ""}
        />

        <SectionAttendanceZoneBoundary
          color={payload?.color}
          schoolBoundaries={payload?.schoolBoundaries ?? []}
          schoolNameFallback={payload?.name ?? ""}
        />

        <RoleBasedRender
          allowedRoles={ROLES_CAN_SEE_FILES}
          scope="global"
        >
          <Documents
            files={payload?.files ?? []}
            goToDriveHref={buildSchoolDriveHref(payload)}
          />
        </RoleBasedRender>
      </div>
    );
  },
);

export default SchoolPanelContent;
